import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger } from '@app/core/logger/logger.service';
import { HealthSystemDto } from '@app/modules/identification/models/Rules';
import { Contract } from '@app/shared/models/contract.model';
import { environment } from '@env/environment';
import { Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';

const log = new Logger('ContractsService');
export const routes = {
  contractsByHealthSystemId: (healthSystemId: string) =>
    `${environment.api.contractServiceBaseURL}ContractByHealthSystemId/${healthSystemId}`,
};

@Injectable({
  providedIn: 'root',
})
export class ContractsService {
  constructor(private readonly http: HttpClient) {}

  contractWithFacilityDisplayMap: Map<number, string> = new Map<number, string>();
  contractFacilitiesListMap: Map<number, string[]> = new Map<number, string[]>();
  contractMasterIdToContractIdMap: Map<number, number> = new Map<number, number>();

  getAllContractsByHealthSystemId(healthSystemId: string): Observable<Contract[]> {
    return this.http
      .get<Contract[]>(routes.contractsByHealthSystemId(healthSystemId))
      .pipe(tap((data) => this.handleContractLoadSideAffects(data)));
  }

  getAllHealthSystems(): Observable<HealthSystemDto[]> {
    return this.http
      .get<
        { healthSystemId: string; healthSystemName: string }[]
      >(`${environment.api.globalServiceBaseURL}health-systems`)
      .pipe(
        map((data) => data.map((hs) => ({ healthSystemId: hs.healthSystemId, healthSystemName: hs.healthSystemName })))
      );
  }

  // checks cache for the contract with facility display name. Allows for a fallback name in the event contracts are not loaded at time of execution
  getContractDisplayNameWithFacility(contractMasterId: number, fallbackName = ''): string {
    if (this.contractWithFacilityDisplayMap.has(contractMasterId)) {
      return this.contractWithFacilityDisplayMap.get(contractMasterId);
    }

    return fallbackName;
  }

  // checks cache for the array of contract facilities
  getContractFacilities(contractMasterId: number): string[] {
    if (this.contractFacilitiesListMap.has(contractMasterId)) {
      return this.contractFacilitiesListMap.get(contractMasterId);
    }

    return [];
  }

  // checks cache for the latest contract for a given contractMasterId
  getLatestContractId(contractMasterId: number): number {
    if (this.contractMasterIdToContractIdMap.has(contractMasterId)) {
      return this.contractMasterIdToContractIdMap.get(contractMasterId);
    }

    return -1;
  }

  private handleContractLoadSideAffects(contracts: Contract[]) {
    log.debug('getAllContractsByHealthSystemId :', contracts);
    this.updateContractCaches(contracts);
  }

  private updateContractCaches(contracts: Contract[]) {
    for (const contract of contracts) {
      this.contractMasterIdToContractIdMap.set(contract.contractMasterId, contract.contractId);

      const facilities = contract.facility?.split(',') ?? [];
      this.contractFacilitiesListMap.set(contract.contractMasterId, facilities);

      const singleFacilityWithContractName =
        facilities.length > 0 ? `${contract.contractName} - ${facilities[0]}` : contract.contractName;

      this.contractWithFacilityDisplayMap.set(contract.contractMasterId, singleFacilityWithContractName);
    }
  }
}
